import { type FormEvent, type JSX, useEffect, useMemo, useRef, useState } from "react";
import {
  completeWebStudioSignIn,
  startWebStudioSignIn,
  type WebStudioSignInAvailabilityView,
} from "../../api/auth";
import type { WebStudioScope } from "../../api/boot";
import { WebStudioCloudApiClient, type WebStudioDiagnostic } from "../../api/generatedClient";
import { createWebStudioFetchTransport } from "../../api/transport";
import { Button, Input } from "../../design";
import {
  clearSignInCodeStep,
  recallSignInCodeStep,
  rememberSignInCodeStep,
} from "./signInFlowMemory";

type FlowStep =
  | { kind: "email" }
  | { kind: "code"; email: string; provider: string; expiresIn: number | null }
  | { kind: "verifying"; email: string; provider: string; expiresIn: number | null };

type FlowFailure =
  | { kind: "diagnostic"; diagnostic: WebStudioDiagnostic }
  | { kind: "transport"; message: string };

export interface SignInCodeFlowProps {
  scope: WebStudioScope;
  availability: WebStudioSignInAvailabilityView;
  /** Invoked after the backend confirms the session; callers re-run boot. */
  onSignedIn: () => void;
  /**
   * Monotonic count of signed-out boot snapshots delivered while gated. A
   * new signed-out boot arriving during the verifying step means the
   * accepted code did not produce a usable session (for example the cookie
   * was not retained): the flow falls back to code entry with a typed
   * failure instead of confirming forever. Optional: compositions that the
   * root gate shadows for signed-out sessions (the Account panel) omit it.
   */
  signedOutBootCount?: number;
}

/**
 * Backend-driven email-code sign-in flow: the start operation emails a
 * verification code, the generated `authenticate` operation completes it,
 * and on success the backend sets browser-inaccessible session cookies —
 * this component only renders typed outcomes and never handles tokens.
 */
export function SignInCodeFlow({
  scope,
  availability,
  onSignedIn,
  signedOutBootCount = 0,
}: SignInCodeFlowProps): JSX.Element | null {
  const client = useMemo(() => new WebStudioCloudApiClient(createWebStudioFetchTransport()), []);
  // Boot refreshes while gated can remount this component; a code that was
  // already emailed must survive the remount so the user can still enter it.
  const [step, setStep] = useState<FlowStep>(() => {
    const remembered = recallSignInCodeStep();
    return remembered === null ? { kind: "email" } : { kind: "code", ...remembered };
  });
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [pending, setPending] = useState(false);
  const [failure, setFailure] = useState<FlowFailure | null>(null);

  const verifyingSinceBoot = useRef<number | null>(null);
  useEffect(() => {
    if (step.kind !== "verifying") {
      return;
    }
    if (verifyingSinceBoot.current !== null && signedOutBootCount > verifyingSinceBoot.current) {
      verifyingSinceBoot.current = null;
      rememberSignInCodeStep({
        email: step.email,
        provider: step.provider,
        expiresIn: step.expiresIn,
      });
      setStep({
        kind: "code",
        email: step.email,
        provider: step.provider,
        expiresIn: step.expiresIn,
      });
      setCode("");
      setFailure({
        kind: "transport",
        message:
          "the backend still reports this browser as signed out, so the session was not established. Enter the code again or request a new one.",
      });
    }
  }, [signedOutBootCount, step]);

  const startAction = availability.actions.find((action) => action.kind === "email_code_start");
  const completeAction = availability.actions.find(
    (action) => action.kind === "email_code_complete",
  );
  if (startAction === undefined || completeAction === undefined) {
    return null;
  }

  const submitEmail = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    if (pending || email.trim() === "") {
      return;
    }
    setPending(true);
    setFailure(null);
    try {
      const outcome = await startWebStudioSignIn(client, scope, { email: email.trim() });
      if (outcome.kind === "code_sent") {
        rememberSignInCodeStep({
          email: outcome.email,
          provider: outcome.provider,
          expiresIn: outcome.expiresIn,
        });
        setStep({
          kind: "code",
          email: outcome.email,
          provider: outcome.provider,
          expiresIn: outcome.expiresIn,
        });
        setCode("");
      } else {
        setFailure({ kind: "diagnostic", diagnostic: outcome.diagnostic });
      }
    } catch (error) {
      setFailure({
        kind: "transport",
        message: error instanceof Error ? error.message : String(error),
      });
    } finally {
      setPending(false);
    }
  };

  const submitCode = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    if (pending || step.kind !== "code" || code.trim() === "") {
      return;
    }
    setPending(true);
    setFailure(null);
    try {
      const outcome = await completeWebStudioSignIn(client, scope, {
        email: step.email,
        code: code.trim(),
      });
      if (outcome.kind === "authenticated") {
        verifyingSinceBoot.current = signedOutBootCount;
        setStep({
          kind: "verifying",
          email: step.email,
          provider: step.provider,
          expiresIn: step.expiresIn,
        });
        onSignedIn();
      } else {
        setFailure({ kind: "diagnostic", diagnostic: outcome.diagnostic });
      }
    } catch (error) {
      setFailure({
        kind: "transport",
        message: error instanceof Error ? error.message : String(error),
      });
    } finally {
      setPending(false);
    }
  };

  if (step.kind === "verifying") {
    return (
      <p className="muted" role="status">
        Code accepted. Confirming the authorized session with the backend…
      </p>
    );
  }

  return (
    <div className="auth-gate-flow">
      {step.kind === "email" ? (
        <form className="auth-gate-flow__form" onSubmit={(event) => void submitEmail(event)}>
          <Input
            label="Email"
            type="email"
            name="email"
            autoComplete="email"
            required
            value={email}
            onChange={(event) => setEmail(event.currentTarget.value)}
            hint="We email a one-time sign-in code to your account address."
          />
          <Button type="submit" disabled={pending || email.trim() === ""}>
            {startAction.label}
          </Button>
        </form>
      ) : (
        <form className="auth-gate-flow__form" onSubmit={(event) => void submitCode(event)}>
          <p className="muted" role="status">
            We emailed a sign-in code to <span className="mono">{step.email}</span>
            {step.expiresIn === null ? "." : `. It expires in ${step.expiresIn} seconds.`}
          </p>
          <Input
            label="Verification code"
            name="code"
            mono
            autoComplete="one-time-code"
            inputMode="numeric"
            required
            value={code}
            onChange={(event) => setCode(event.currentTarget.value)}
          />
          <div className="auth-gate-flow__actions">
            <Button type="submit" disabled={pending || code.trim() === ""}>
              {completeAction.label}
            </Button>
            <Button
              type="button"
              variant="ghost"
              disabled={pending}
              onClick={() => {
                clearSignInCodeStep();
                setStep({ kind: "email" });
                setCode("");
                setFailure(null);
              }}
            >
              Use a different email
            </Button>
          </div>
        </form>
      )}
      {pending ? (
        <p className="muted" role="status">
          Waiting for the backend…
        </p>
      ) : null}
      {failure === null ? null : (
        <div className="auth-gate-flow__failure" role="alert">
          {failure.kind === "diagnostic" ? (
            <>
              <p className="auth-gate-flow__diagnostic">
                <span className="mono">{failure.diagnostic.code}</span> {failure.diagnostic.message}
              </p>
              {step.kind === "code" ? (
                <div className="auth-gate-flow__actions">
                  <Button type="button" variant="secondary" onClick={() => setCode("")}>
                    Enter a different code
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={() => {
                      clearSignInCodeStep();
                      setStep({ kind: "email" });
                      setCode("");
                      setFailure(null);
                    }}
                  >
                    Request a new code
                  </Button>
                </div>
              ) : null}
            </>
          ) : (
            <p className="auth-gate-flow__diagnostic">
              The sign-in request did not reach the backend: {failure.message}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
